import { ErrorBoundary } from '@/components/ErrorBoundary';
import { ExclamationCircleIcon } from '@heroicons/react/24/outline';
import type * as Sentry from '@sentry/react';
import type { ReactNode } from 'react';

// Defensive boundary for the public order/checkout page. A single malformed offer
// or a price-calculation error must never white-screen the whole page — a blank
// checkout is lost revenue. The order page's offer rendering and the
// selected-offer price breakdown are wrapped in this boundary so a render error
// in one section shows a calm fallback (with a way to recover) instead of
// unmounting the React tree. See the `/order/*` hardening work.

// Tagged client-side log so a recurrence leaves a trace. The shared ErrorBoundary
// wraps Sentry's, which already reports the exception to Sentry; this adds a
// clearly-tagged console breadcrumb — the original white-screen left none because
// the tree simply unmounted.
const onOrderError: Sentry.ErrorBoundaryProps['onError'] = (error, componentStack) => {
  // eslint-disable-next-line no-console
  console.error(
    '[order] checkout render error — offer/price section failed',
    error,
    componentStack,
  );
};

const linkClass =
  'font-medium text-emerald-600 underline underline-offset-2 hover:text-emerald-500 dark:text-emerald-400 dark:hover:text-emerald-300';

const OrderErrorFallback = ({ compact }: { compact?: boolean }) => (
  <div
    className={
      compact ? 'px-5 py-6 sm:px-7' : 'flex grow flex-col items-center justify-center py-10'
    }
  >
    <div className="mx-auto flex max-w-md flex-col items-center gap-3 rounded-2xl border border-zinc-200 bg-white p-6 text-center dark:border-zinc-700 dark:bg-zinc-900">
      <ExclamationCircleIcon className="size-6 shrink-0 text-amber-500" aria-hidden="true" />
      <p className="text-sm text-zinc-700 leading-relaxed dark:text-zinc-200">
        Something went wrong loading this option. Please{' '}
        <a
          href="#"
          onClick={(e) => {
            e.preventDefault();
            location.reload();
          }}
          className={linkClass}
        >
          refresh
        </a>{' '}
        or contact support at{' '}
        <a href="mailto:admin@raspicks.com" className={linkClass}>
          admin@raspicks.com
        </a>
        .
      </p>
    </div>
  </div>
);

/**
 * Wraps a slice of the order page so a render error in that slice degrades to a
 * graceful fallback rather than blanking the page. Pass `compact` for an inline
 * section (keeps the surrounding card chrome) versus the standalone centered
 * fallback used when wrapping the whole product view.
 */
export function OrderErrorBoundary({
  children,
  compact,
}: {
  children: ReactNode;
  compact?: boolean;
}) {
  return (
    <ErrorBoundary fallback={<OrderErrorFallback compact={compact} />} onError={onOrderError}>
      {children}
    </ErrorBoundary>
  );
}
